import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { PageHeader, Card, Table, Badge, InfoAlert, Pagination, FilterBar, FilterSelect, StatsGrid, StatCard, Btn } from '../components/UI';
import { fmt } from '../utils/helpers';

/**
 * Free-hour credits granted for outages.
 *
 * The point of this page is to answer "is compensation actually reaching
 * people, or just expiring unused?" — which auto-extending expiry could never
 * show, because there was nothing to redeem.
 */
const STATUS_COLOR = { available: 'blue', used: 'green', expired: 'gray', revoked: 'red' };

function humanMins(m) {
  if (!m) return '0m';
  const h = Math.floor(m / 60), rem = m % 60;
  if (!h) return `${rem}m`;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

export default function Credits() {
  const [rows, setRows]         = useState([]);
  const [summary, setSummary]   = useState(null);
  const [recent, setRecent]     = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [status, setStatus]     = useState('all');
  const [loading, setLoading]   = useState(true);
  const [busyId, setBusyId]     = useState(null);
  const toast = useToast();

  const load = useCallback(async (p = 1, st = status) => {
    setLoading(true);
    try {
      const d = await api.getCredits({ page: p, limit: 25, ...(st !== 'all' ? { status: st } : {}) });
      setRows(d.credits || []);
      setSummary(d.summary || null);
      setRecent(d.recent_activations || []);
      setTotal(d.total || 0);
    } catch (e) { toast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [status]); // eslint-disable-line

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  async function revoke(id) {
    if (!window.confirm('Revoke this unused credit?')) return;
    setBusyId(id);
    try { await api.revokeCredit(id); toast('Credit revoked.', 'success'); load(page); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusyId(null); }
  }

  const granted = Number(summary?.granted_minutes || 0);
  const used    = Number(summary?.used_minutes || 0);
  const redemption = granted ? Math.round((used / granted) * 100) : 0;

  const columns = [
    {
      key: 'user', label: 'User',
      render: (c) => c.user
        ? <div>
            <div style={{ fontSize: 12, color: 'var(--text)' }}>{c.user.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.user.email}</div>
          </div>
        : <span style={{ color: 'var(--text3)' }}>—</span>,
    },
    {
      key: 'source', label: 'Source',
      render: (c) => c.outage
        ? <div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>{c.outage.lodge?.name || 'Outage'}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{fmt.date(c.outage.started_at)}</div>
          </div>
        : <Badge color="purple">admin grant</Badge>,
    },
    {
      key: 'minutes', label: 'Granted / used',
      render: (c) => (
        <div style={{ fontSize: 12 }}>
          <span style={{ color: 'var(--text)', fontWeight: 600 }}>{humanMins(c.minutes)}</span>
          <span style={{ color: 'var(--text3)' }}> · used {humanMins(c.minutes_used)}</span>
        </div>
      ),
    },
    { key: 'status', label: 'Status', render: (c) => <Badge color={STATUS_COLOR[c.status] || 'gray'}>{c.status}</Badge> },
    { key: 'expires', label: 'Expires', render: (c) => <span style={{ fontSize: 12, color: 'var(--text3)' }}>{fmt.date(c.expires_at)}</span> },
    {
      key: 'actions', label: '',
      render: (c) => c.status === 'available' && c.minutes_used === 0
        ? <Btn size="xs" variant="ghost" loading={busyId === c.id} onClick={() => revoke(c.id)}>Revoke</Btn>
        : null,
    },
  ];

  return (
    <div>
      <PageHeader title="Free Hours" subtitle="Outage compensation granted to users, and whether they actually use it." />

      <StatsGrid>
        <StatCard label="Granted"   value={humanMins(granted)} sub={`${summary?.grants || 0} grants`} />
        <StatCard label="Redeemed"  value={humanMins(used)}    sub={`${redemption}% of granted`} accent />
        <StatCard label="Available" value={summary?.available || 0} sub="unspent grants" />
        <StatCard label="Expired unused" value={summary?.expired_unused || 0} sub="lapsed without use" />
      </StatsGrid>

      <InfoAlert>
        Credit is banked rather than added to a plan's expiry automatically — a long overnight
        outage used to extend expiry into hours people slept through. A low redemption rate here
        means users aren't seeing or understanding the offer, not that they didn't need it.
      </InfoAlert>

      <FilterBar>
        <FilterSelect
          value={status}
          onChange={(v) => { setStatus(v); setPage(1); load(1, v); }}
          options={[
            { value: 'all',       label: 'All credits' },
            { value: 'available', label: 'Available' },
            { value: 'used',      label: 'Fully used' },
            { value: 'expired',   label: 'Expired unused' },
            { value: 'revoked',   label: 'Revoked' },
          ]}
        />
      </FilterBar>

      <Card style={{ padding: 0, marginTop: 16 }}>
        <Table columns={columns} rows={rows} loading={loading} emptyText="No credits issued yet." />
      </Card>

      <Pagination page={page} total={total} limit={25} onChange={(p) => { setPage(p); load(p); }} />

      {recent.length > 0 && (
        <Card style={{ marginTop: 20 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--text)' }}>Recent activations</h3>
          {recent.map(a => (
            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 12, borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--text2)' }}>{a.user?.email || a.user_id}</span>
              <span style={{ color: 'var(--text)' }}>
                {humanMins(a.minutes)} · <span style={{ color: 'var(--text3)' }}>{a.mode}</span> · {fmt.datetime(a.createdAt)}
              </span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
